import { Stack } from "@mui/material";
import { GeneratorStatusExceedanceDirection } from "generated-graphql/graphql";

type Props = {
  direction?: GeneratorStatusExceedanceDirection | null;
  /** Detected mismatching months folded into the run; null until the next sweep. */
  mismatchMonths?: number | null;
  /** Trailing consecutive months the mismatch has persisted. */
  sustainedMonths?: number | null;
  detectedAt?: string | null;
  validatedAt?: string | null;
  /** How the row was resolved, already labelled; null while it is still open. */
  resolutionMethod?: string | null;
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Whole days between an ISO timestamp and now (>= 0). */
function daysSince(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

/**
 * Lifecycle metadata line under the drawer's status chips: kind of discrepancy,
 * how long its run is, when it was detected/validated, and how it was resolved.
 * Gives the TPM/RS context without leaving the drawer.
 */
export function ExceedanceMetaLine({
  direction,
  mismatchMonths,
  sustainedMonths,
  detectedAt,
  validatedAt,
  resolutionMethod,
}: Props) {
  return (
    <Stack
      direction="row"
      spacing={2}
      flexWrap="wrap"
      sx={{ color: "text.secondary", typography: "body2" }}
    >
      <span>
        {direction === GeneratorStatusExceedanceDirection.Downward
          ? "Downward trend"
          : "Exceedance"}
        {mismatchMonths ? ` · ${mismatchMonths}mo detected` : ""}
        {sustainedMonths ? ` · ${sustainedMonths}mo consecutive` : ""}
      </span>
      {detectedAt && (
        <span>
          First detected {formatDate(detectedAt)} ({daysSince(detectedAt)}d open)
        </span>
      )}
      {validatedAt && <span>Validated {formatDate(validatedAt)}</span>}
      {resolutionMethod && <span>Resolved by: {resolutionMethod}</span>}
    </Stack>
  );
}
